import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useScope } from '../../scope';
import { fromBp, messageOf, toBp, toCents, toDollars, type LoyaltyProgram, type RewardType } from './types';

type ProgramForm = {
  enabledPos: boolean;
  enabledOnline: boolean;
  ordersPerCard: string;
  rewardType: RewardType;
  posAmount: string; // dollars
  onlineAmount: string; // dollars
  posPercent: string; // percent
  onlinePercent: string; // percent
  accrueOnDiscounted: boolean;
  redeemOnDiscounted: boolean;
};

const BLANK: ProgramForm = {
  enabledPos: false, enabledOnline: false, ordersPerCard: '10', rewardType: 'percent',
  posAmount: '', onlineAmount: '', posPercent: '', onlinePercent: '',
  accrueOnDiscounted: true, redeemOnDiscounted: true,
};

function formOf(p: LoyaltyProgram): ProgramForm {
  return {
    enabledPos: p.enabledPos,
    enabledOnline: p.enabledOnline,
    ordersPerCard: String(p.ordersPerCard),
    rewardType: p.rewardType,
    posAmount: toDollars(p.posAmountInCents),
    onlineAmount: toDollars(p.onlineAmountInCents),
    posPercent: fromBp(p.posPercentBp),
    onlinePercent: fromBp(p.onlinePercentBp),
    accrueOnDiscounted: p.accrueOnDiscounted,
    redeemOnDiscounted: p.redeemOnDiscounted,
  };
}

export function Loyalty() {
  const { rid } = useScope();
  const R = `/restaurants/${rid}`;
  const [form, setForm] = useState<ProgramForm | null>(null);
  const [configured, setConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await api.get<LoyaltyProgram | null>(`${R}/loyalty-program`);
      setForm(p ? formOf(p) : { ...BLANK });
      setConfigured(p !== null);
      setError(null);
    } catch (e) {
      setError(messageOf(e, 'Failed to load.'));
    }
  }, [R]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!form) return;
    const flat = form.rewardType === 'flat_amount';
    // The pairing rule the API enforces: flat_amount carries both dollar values
    // and null percents; percent carries both percents and null dollar values.
    const body: LoyaltyProgram = {
      enabledPos: form.enabledPos,
      enabledOnline: form.enabledOnline,
      ordersPerCard: parseInt(form.ordersPerCard, 10) || 0,
      rewardType: form.rewardType,
      posAmountInCents: flat ? toCents(form.posAmount) : null,
      onlineAmountInCents: flat ? toCents(form.onlineAmount) : null,
      posPercentBp: flat ? null : toBp(form.posPercent),
      onlinePercentBp: flat ? null : toBp(form.onlinePercent),
      accrueOnDiscounted: form.accrueOnDiscounted,
      redeemOnDiscounted: form.redeemOnDiscounted,
    };
    setBusy(true);
    try {
      const p = await api.put<LoyaltyProgram>(`${R}/loyalty-program`, body);
      setForm(formOf(p));
      setConfigured(true);
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'invalid_program') {
        setError(
          form.rewardType === 'flat_amount'
            ? 'The program needs a dollar reward for both POS and online (and at least 1 order per card).'
            : 'The program needs a percentage reward for both POS and online (and at least 1 order per card).',
        );
      } else {
        setError(messageOf(e, 'Save failed.'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (!form) return <div className="loading">{error ?? 'Loading…'}</div>;

  const flat = form.rewardType === 'flat_amount';

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Customers · Loyalty</div>
          <h1>Loyalty program</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--ok)', fontSize: 13.5, fontWeight: 700 }}>Saved ✓</span>}
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : configured ? 'Save program' : 'Create program'}
          </button>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {!configured && <div className="loading" style={{ paddingTop: 0 }}>No program configured yet — set one up below.</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" data-section="channels">
            <h2>Channels</h2>
            <div style={{ display: 'flex', gap: 24, padding: '12px 0', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={form.enabledPos}
                  onChange={(e) => setForm({ ...form, enabledPos: e.target.checked })} />
                <b style={{ fontSize: 14 }}>POS orders accrue</b>
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={form.enabledOnline}
                  onChange={(e) => setForm({ ...form, enabledOnline: e.target.checked })} />
                <b style={{ fontSize: 14 }}>Online orders accrue</b>
              </label>
            </div>
          </div>

          <div className="panel" data-section="reward">
            <h2>Reward</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, paddingTop: 8 }}>
              <div className="field">
                <label>Orders per card</label>
                <input className="input" aria-label="Orders per card" type="number" min={1} value={form.ordersPerCard}
                  onChange={(e) => setForm({ ...form, ordersPerCard: e.target.value })} />
              </div>
              <div className="field">
                <label>Reward type</label>
                <select className="input" value={form.rewardType}
                  aria-label="Reward type" onChange={(e) => setForm({ ...form, rewardType: e.target.value as RewardType })}>
                  <option value="flat_amount">Flat amount per order</option>
                  <option value="percent">Percent of the order</option>
                </select>
              </div>
              {flat ? (
                <>
                  <div className="field">
                    <label>POS reward ($ / order)</label>
                    <input className="input" aria-label="POS reward dollars" type="number" min={0} step="0.01" value={form.posAmount}
                      onChange={(e) => setForm({ ...form, posAmount: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Online reward ($ / order)</label>
                    <input className="input" aria-label="Online reward dollars" type="number" min={0} step="0.01" value={form.onlineAmount}
                      onChange={(e) => setForm({ ...form, onlineAmount: e.target.value })} />
                  </div>
                </>
              ) : (
                <>
                  <div className="field">
                    <label>POS reward (% of order)</label>
                    <input className="input" aria-label="POS reward percent" type="number" min={0} max={100} step="0.01" value={form.posPercent}
                      onChange={(e) => setForm({ ...form, posPercent: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Online reward (% of order)</label>
                    <input className="input" aria-label="Online reward percent" type="number" min={0} max={100} step="0.01" value={form.onlinePercent}
                      onChange={(e) => setForm({ ...form, onlinePercent: e.target.value })} />
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={form.accrueOnDiscounted}
                  onChange={(e) => setForm({ ...form, accrueOnDiscounted: e.target.checked })} />
                Discounted orders still accrue
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input type="checkbox" checked={form.redeemOnDiscounted}
                  onChange={(e) => setForm({ ...form, redeemOnDiscounted: e.target.checked })} />
                Cards redeemable on discounted orders
              </label>
            </div>
          </div>
        </div>

        <div className="panel" data-section="explainer">
          <h2>How accrual works</h2>
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.65, paddingTop: 8 }}>
            <p style={{ margin: '0 0 10px' }}>
              Every qualifying order banks a reward onto the customer's open card — a fixed dollar
              amount or a percentage of the order, per the reward type above (POS and online orders can
              earn at different rates).
            </p>
            <p style={{ margin: '0 0 10px' }}>
              Once the card holds <b>{form.ordersPerCard || '…'}</b> rewards it is complete: the
              banked total becomes a credit the customer can spend as a discount on a future order.
            </p>
            <p style={{ margin: 0 }}>
              Redeeming closes the card and a fresh one opens on their next qualifying order.
              Customers' cards and history live on their profile in the directory.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
